import { Col, Image, Row } from "react-bootstrap-v5";
import { InterviewSession, VJFPUser, Booth } from "app/API";
import { getInitials, getStyleByIndex, UrlParamReplace } from "app/helpers/MiscUtils";
import { useEffect, useState, useCallback, useMemo } from "react";
import { BoothService } from "app/services/BoothService";
import { AppTopNav } from "app/modules/shared/AppTopNav";
import { BasePageProps } from "app/types";
import { InterviewSessionService } from "app/services/InterviewSessionService";
import { JSRoutes } from "../JSPage";
import { Mailbox2, Building, Calendar3, ChatLeftText } from "react-bootstrap-icons";
import { VJFPUserService } from "app/services/VJFPUserService";
import { parse } from "zipson";
import { useHistory } from "react-router";
import { useAuth } from "app/context/auth-context";
import { useAppContext } from "app/context/app-context";
import { useS3Object } from "app/context/S3ObjectContext";
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import moment from "moment";
import { HMSMessage } from "@100mslive/react-sdk";
import { ChatMessage } from "app/modules/shared/100ms-chat/chat-message";

interface InterviewSessionsItem extends InterviewSession {
    recruiter?: VJFPUser;
    boothsInfo?: Booth;
}

// Singleton services - instantiate once outside component
const interviewSessionService = new InterviewSessionService();
const vjfpUserService = new VJFPUserService();
const boothService = new BoothService();

const JSHistory = (props: BasePageProps) => {
    const { user } = useAuth();
    const { jobFair } = useAppContext();

    const [interviewSessions, setInterviewSessions] = useState<InterviewSessionsItem[]>([]);
    const [chatRecords, setChatRecords] = useState<HMSMessage[]>([]);
    const [activeInterviewSession, setActiveInterviewSession] = useState<InterviewSession | undefined>(undefined);
    const [chatRecruiter, setChatRecruiter] = useState<VJFPUser | undefined>(undefined);
    const { getS3ObjectUrl, s3ObjectCache } = useS3Object();
    const [loading, setLoading] = useState<boolean>(false);
    const [chatLoading, setChatLoading] = useState<boolean>(false);

    // Memoized enrichment function
    const enrichSession = useCallback(async (item: InterviewSessionsItem, organizationId: string, jobFairId: string) => {
        const enrichedItem = { ...item };

        // Parallel fetch recruiter and booth info
        const [recruiter, booth] = await Promise.allSettled([
            item?.recruiterId ? vjfpUserService.getById(organizationId, item.recruiterId) : Promise.resolve(undefined),
            item?.boothId ? boothService.getBoothById(jobFairId, item.boothId) : Promise.resolve(undefined)
        ]);

        if (recruiter.status === 'fulfilled' && recruiter.value) {
            const recruiterData = recruiter.value as VJFPUser;
            if (recruiterData.avatar) {
                getS3ObjectUrl(recruiterData.avatar);
            }
            enrichedItem.recruiter = recruiterData;
        } else if (recruiter.status === 'rejected') {
            console.error('Failed to load recruiter for session', item.id, recruiter.reason);
        }

        if (booth.status === 'fulfilled' && booth.value) {
            enrichedItem.boothsInfo = booth.value;
        } else if (booth.status === 'rejected') {
            console.error('Failed to load booth for session', item.id, booth.reason);
        }

        return enrichedItem;
    }, []);

    // Load interview sessions
    useEffect(() => {
        if (!user?.username || !user?.id || !user?.organizationId || !jobFair?.id) return;

        let mounted = true;
        setLoading(true);

        const loadSessions = async () => {
            try {
                const _interviewSessions = await interviewSessionService.getInterviewsByJobseeker(jobFair.id, user.id);
                if (!_interviewSessions || !mounted) return;

                // Enrich all sessions in parallel
                const enriched = await Promise.all(
                    _interviewSessions.map(item => enrichSession(item, item.organizationId!, jobFair.id))
                );

                // Sort by createdAt desc
                enriched.sort((a, b) => moment(b?.createdAt).diff(moment(a?.createdAt)));

                if (!mounted) return;
                setInterviewSessions(enriched);

                // Auto-select first session
                if (enriched.length > 0 && enriched[0].id) {
                    onActiveInterviewSessionIdChanged(jobFair.id, enriched[0].id);
                }
            } catch (err) {
                console.error('Failed to load interview sessions:', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadSessions();

        return () => { mounted = false; };
    }, [user?.username, user?.id, user?.organizationId, jobFair?.id, enrichSession]);

    // Parse chat transcript when active session changes
    useEffect(() => {
        if (!activeInterviewSession?.chatTranscript) {
            setChatRecords([]);
            return;
        }

        try {
            const parsed = parse(activeInterviewSession.chatTranscript) as HMSMessage[];
            setChatRecords(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
            console.error('Failed to parse chat transcript:', error);
            setChatRecords([]);
        }
    }, [activeInterviewSession?.chatTranscript]);

    // Handle active session change
    const onActiveInterviewSessionIdChanged = useCallback(async (jobFairId: string, id?: string) => {
        if (!id) {
            setActiveInterviewSession(undefined);
            setChatRecruiter(undefined);
            return;
        }

        setChatLoading(true);
        try {
            const _interviewSession = await interviewSessionService.getById(jobFairId, id);
            if (!_interviewSession) {
                setActiveInterviewSession(undefined);
                setChatRecruiter(undefined);
                return;
            }

            setActiveInterviewSession(_interviewSession);

            // Load recruiter if available
            if (_interviewSession.organizationId && _interviewSession.recruiterId) {
                try {
                    const recruiter = await vjfpUserService.getById(_interviewSession.organizationId, _interviewSession.recruiterId);
                    if (recruiter?.avatar) {
                        getS3ObjectUrl(recruiter.avatar);
                    }
                    setChatRecruiter(recruiter);
                } catch (err) {
                    console.error('Failed to load recruiter for active session:', err);
                    setChatRecruiter(undefined);
                }
            } else {
                setChatRecruiter(undefined);
            }
        } catch (err) {
            console.error('Error loading active interview session:', err);
            setActiveInterviewSession(undefined);
            setChatRecruiter(undefined);
        } finally {
            setChatLoading(false);
        }
    }, [user?.organizationId]);

    return (
        <div className="bg-light min-vh-100">
            <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />

            <div className="container-fluid px-4 py-4">
                <Row className="g-4">
                    {/* Left Column - Interview Sessions List */}
                    <Col md={7}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white border-bottom py-3">
                                <div className="d-flex align-items-center">
                                    <ChatLeftText size={20} className="text-primary me-2" />
                                    <h5 className="mb-0 fw-bold">My Chats</h5>
                                </div>
                                <p className="text-muted mb-0 mt-1 small">
                                    View your interview conversation history
                                </p>
                            </div>
                            <div className="card-body p-0">
                                <BlockUi tag="div" blocking={loading}>
                                    {!loading && interviewSessions.length === 0 ? (
                                        <div className="text-center py-5 px-3">
                                            <div className="mb-4">
                                                <Mailbox2 color="#6c757d" size={80} style={{ opacity: 0.5 }} />
                                            </div>
                                            <h4 className="text-muted mb-2">No Conversations Yet</h4>
                                            <p className="text-muted mb-0" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                                Your interview conversations will appear here once you've completed interviews with recruiters.
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ minHeight: '400px' }}>
                                            <Table
                                                interviewSessions={interviewSessions}
                                                onActiveInterviewSessionIdChanged={onActiveInterviewSessionIdChanged}
                                                activeSessionId={activeInterviewSession?.id}
                                                s3ObjectCache={s3ObjectCache}
                                            />
                                        </div>
                                    )}
                                </BlockUi>
                            </div>
                        </div>
                    </Col>

                    {/* Right Column - Chat Transcript */}
                    <Col md={5}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white border-bottom py-3">
                                {chatRecruiter ? (
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            {chatRecruiter.avatar && s3ObjectCache[chatRecruiter.avatar] ? (
                                                <Image
                                                    src={s3ObjectCache[chatRecruiter.avatar]}
                                                    roundedCircle
                                                    crossOrigin="anonymous"
                                                    style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div
                                                    className="d-flex align-items-center justify-content-center text-white rounded-circle"
                                                    style={{
                                                        width: '45px',
                                                        height: '45px',
                                                        backgroundColor: '#0d6efd',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {getInitials(chatRecruiter.firstName, chatRecruiter.lastName)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="mb-0 fw-bold">
                                                {chatRecruiter.firstName} {chatRecruiter.lastName}
                                            </h5>
                                            {chatRecruiter.designation && (
                                                <p className="mb-1 text-muted small">
                                                    <strong>Position:</strong> {chatRecruiter.designation}
                                                </p>
                                            )}
                                            {chatRecruiter.address && (
                                                <p className="mb-0 text-muted small fst-italic">
                                                    <Building size={14} className="me-1" />
                                                    {chatRecruiter.address}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-2">
                                        <h6 className="mb-0 text-muted">
                                            <ChatLeftText size={18} className="me-2" />
                                            Select a conversation to view details
                                        </h6>
                                    </div>
                                )}
                            </div>
                            <div className="card-body p-0" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                                <BlockUi
                                    tag="div"
                                    blocking={chatLoading}
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                                >
                                    <div
                                        className="p-3 bg-light overflow-auto"
                                        style={{
                                            flex: 1,
                                            backgroundImage: 'linear-gradient(to bottom, #f8f9fa, #e9ecef)'
                                        }}
                                    >
                                        {!activeInterviewSession ? (
                                            <div className="d-flex align-items-center justify-content-center h-100">
                                                <div className="text-center">
                                                    <ChatLeftText size={64} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                    <h5 className="text-muted mb-2">No Conversation Selected</h5>
                                                    <p className="text-muted small mb-0">
                                                        Choose an interview from the list to view the chat history
                                                    </p>
                                                </div>
                                            </div>
                                        ) : chatRecords.length > 0 ? (
                                            <div className="d-flex flex-column gap-3">
                                                {chatRecords.map((record) => (
                                                    <ChatMessage key={record.id} message={record} />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100">
                                                <div className="text-center">
                                                    <Mailbox2 size={64} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                    <h5 className="text-muted mb-2">No Messages</h5>
                                                    <p className="text-muted small mb-0">
                                                        There was no text chat during this conversation
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </BlockUi>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

// Enhanced table component with better styling
const Table = ({ interviewSessions, onActiveInterviewSessionIdChanged, activeSessionId, s3ObjectCache }: {
    interviewSessions: InterviewSessionsItem[];
    onActiveInterviewSessionIdChanged: (jobFairId: string, id: string | undefined) => void;
    activeSessionId?: string;
    s3ObjectCache: { [key: string]: string | undefined };
}) => {

    const handleRowClick = useCallback((jobFairId: string, id?: string) => {
        onActiveInterviewSessionIdChanged(jobFairId, id);
    }, [onActiveInterviewSessionIdChanged]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent, jobFairId: string, id?: string) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick(jobFairId, id);
        }
    }, [handleRowClick]);

    // Helper to get avatar background color
    const getAvatarColor = (index: number) => {
        const colors = ['#0d6efd', '#198754', '#dc3545', '#ffc107', '#0dcaf0', '#6610f2'];
        return colors[index % colors.length];
    };

    return (
        <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                    <tr>
                        <th className="border-0 text-muted fw-semibold py-3 ps-4" style={{ minWidth: '250px' }}>
                            Recruiter
                        </th>
                        <th className="border-0 text-muted fw-semibold py-3 pe-4 text-end" style={{ minWidth: '200px' }}>
                            Company & Date
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {interviewSessions.map((_iSession, index) => {
                        const isSelected = activeSessionId === _iSession.id;
                        const rowKey = _iSession.id ?? `session-${index}`;
                        const avatarColor = getAvatarColor(index);

                        return (
                            <tr
                                key={rowKey}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyDown(e, _iSession.jobFairId, _iSession?.id)}
                                onClick={() => handleRowClick(_iSession.jobFairId, _iSession?.id)}
                                className={isSelected ? 'table-active' : ''}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: isSelected ? '#e7f1ff' : 'transparent'
                                }}
                                aria-selected={isSelected}
                            >
                                <td className="ps-4 py-3">
                                    <div className="d-flex align-items-center">
                                        {_iSession?.recruiter?.avatar && s3ObjectCache[_iSession.recruiter.avatar] ? (
                                            <Image
                                                src={s3ObjectCache[_iSession.recruiter.avatar]}
                                                crossOrigin="anonymous"
                                                className="rounded-circle shadow-sm"
                                                style={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: 'cover',
                                                    border: isSelected ? '3px solid #0d6efd' : '2px solid #e9ecef'
                                                }}
                                                alt={`${_iSession.recruiter.firstName} ${_iSession.recruiter.lastName}`}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm"
                                                style={{
                                                    width: 50,
                                                    height: 50,
                                                    fontSize: '1.1rem',
                                                    fontWeight: 'bold',
                                                    backgroundColor: avatarColor,
                                                    border: isSelected ? '3px solid #0d6efd' : 'none'
                                                }}
                                            >
                                                {getInitials(_iSession?.recruiter?.firstName, _iSession?.recruiter?.lastName)}
                                            </div>
                                        )}

                                        <div className="ms-3">
                                            <div className="fw-bold text-dark mb-0" style={{ fontSize: '0.95rem' }}>
                                                {_iSession?.recruiter?.firstName} {_iSession?.recruiter?.lastName}
                                            </div>
                                            {_iSession?.recruiter?.designation && (
                                                <div className="text-muted small mt-1">
                                                    {_iSession.recruiter.designation}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>

                                <td className="pe-4 py-3 text-end">
                                    <div>
                                        {_iSession?.boothsInfo?.name && (
                                            <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                                                <Building size={14} className="me-1 text-primary" />
                                                {_iSession.boothsInfo.name}
                                            </div>
                                        )}
                                        {_iSession?.createdAt && (
                                            <div className="text-muted small">
                                                <Calendar3 size={13} className="me-1" />
                                                {moment(_iSession.createdAt).format('MMM DD, YYYY')}
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default JSHistory;
